/**
 * Presentation Service
 *
 * Service for fetching and managing presentations from the backend API.
 * Handles communication with the presentation backend endpoints.
 * Falls back to static presentation data when backend is unavailable (offline mode).
 */

import type { PresentationData } from '../types'
import { API_CONFIG, API_ENDPOINTS } from '../config/api.config'
import { getStaticPresentationLibrary, isOfflineModeEnabled } from '@/config/presentations'

/**
 * Service error class for presentation-specific errors
 */
export class PresentationServiceError extends Error {
  declare public code?: string
  declare public statusCode?: number

  constructor(
    message: string,
    code?: string,
    statusCode?: number
  ) {
    super(message)
    this.name = 'PresentationServiceError'
    this.code = code
    this.statusCode = statusCode
  }
}

/**
 * Result type for service operations that can fail
 */
export type ServiceResult<T> = {
  success: true
  data: T
} | {
  success: false
  error: PresentationServiceError
}

/**
 * Presentation library entry type
 */
export interface PresentationLibraryEntry {
  slug: string
  data: PresentationData
}

/**
 * Raw API response types
 */
interface RawLibraryResponse {
  success: boolean
  presentations: PresentationLibraryEntry[]
  total: number
}

interface RawPresentationMetadataResponse {
  success: boolean
  data?: {
    id: string
    title: string
    description: string
    category: string
    language: string
    thumbnailUrl: string
    duration: string
    slideCount: number
    tags: string[]
    isActive: boolean
    createdAt: string
    updatedAt: string
  }
  error?: {
    code: string
    message: string
  }
}

interface RawApiResponse<T> {
  success: boolean
  data?: T
  error?: {
    code: string
    message: string
  }
}

/**
 * Request options for API calls
 */
interface RequestOptions {
  signal?: AbortSignal
  headers?: Record<string, string>
}

/**
 * Presentation Service configuration interface
 */
export interface PresentationServiceConfig {
  BASE_URL: string
  USE_BACKEND: boolean
  TIMEOUT: number
  RETRY_ATTEMPTS: number
  RETRY_DELAY: number
}

/**
 * Presentation Service class
 *
 * Provides methods for interacting with the presentation backend API.
 * All methods return results that handle success/failure explicitly.
 */
export class PresentationService {
  private readonly baseUrl: string
  private readonly timeout: number
  private readonly retryAttempts: number
  private readonly retryDelay: number

  constructor(config?: Partial<PresentationServiceConfig>) {
    const finalConfig = { ...API_CONFIG, ...config }
    this.baseUrl = finalConfig.BASE_URL
    this.timeout = finalConfig.TIMEOUT
    this.retryAttempts = finalConfig.RETRY_ATTEMPTS
    this.retryDelay = finalConfig.RETRY_DELAY
  }

  /**
   * Make a fetch request with timeout and retry logic
   */
  private async fetchWithRetry<T>(
    endpoint: string,
    options: RequestInit & { signal?: AbortSignal } = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), this.timeout)

    // Combine user signal with timeout signal
    const signal = options.signal
      ? this.combineSignals([controller.signal, options.signal])
      : controller.signal

    let lastError: Error | null = null

    for (let attempt = 0; attempt < this.retryAttempts; attempt++) {
      try {
        const response = await fetch(url, {
          ...options,
          signal,
          headers: {
            'Content-Type': 'application/json',
            ...options.headers,
          },
        })

        clearTimeout(timeoutId)

        if (!response.ok) {
          // Don't retry on client errors (4xx)
          if (response.status >= 400 && response.status < 500) {
            const errorData = await this.tryParseError(response)
            throw new PresentationServiceError(
              errorData?.message || `HTTP ${response.status}: ${response.statusText}`,
              errorData?.code,
              response.status
            )
          }
          // Retry on server errors (5xx)
          throw new PresentationServiceError(
            `HTTP ${response.status}: ${response.statusText}`,
            'SERVER_ERROR',
            response.status
          )
        }

        return await response.json()
      } catch (error) {
        lastError = error as Error

        // Don't retry if it's a PresentationServiceError with a 4xx status
        if (error instanceof PresentationServiceError && error.statusCode && error.statusCode < 500) {
          throw error
        }

        // Don't retry if the request was aborted
        if (error instanceof Error && error.name === 'AbortError') {
          throw new PresentationServiceError('Request timeout', 'TIMEOUT')
        }

        // Wait before retrying (except on last attempt)
        if (attempt < this.retryAttempts - 1) {
          await this.delay(this.retryDelay * (attempt + 1))
        }
      }
    }

    clearTimeout(timeoutId)
    throw lastError || new PresentationServiceError('Unknown error occurred')
  }

  /**
   * Combine multiple AbortSignals into one
   */
  private combineSignals(signals: AbortSignal[]): AbortSignal {
    const controller = new AbortController()

    for (const signal of signals) {
      if (signal.aborted) {
        controller.abort()
        break
      }
      signal.addEventListener('abort', () => controller.abort(), { once: true })
    }

    return controller.signal
  }

  /**
   * Try to parse error response JSON
   */
  private async tryParseError(response: Response): Promise<{ code?: string; message?: string } | null> {
    try {
      return await response.json()
    } catch {
      return null
    }
  }

  /**
   * Delay helper for retry logic
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  /**
   * Fetch all presentations from the backend library
   *
   * Falls back to static presentation data when backend is unavailable.
   *
   * @returns Result with array of presentation entries or error
   */
  async fetchLibrary(options?: RequestOptions): Promise<ServiceResult<PresentationLibraryEntry[]>> {
    // If offline mode is enabled, use static data directly
    if (isOfflineModeEnabled()) {
      console.log('[PresentationService] Offline mode enabled - using static presentation data')
      try {
        const staticPresentations = getStaticPresentationLibrary()
        return {
          success: true,
          data: staticPresentations,
        }
      } catch (error) {
        return {
          success: false,
          error: new PresentationServiceError(
            error instanceof Error ? error.message : 'Failed to load static presentations',
            'STATIC_LOAD_ERROR'
          ),
        }
      }
    }

    // Try backend API first
    try {
      const response = await this.fetchWithRetry<RawLibraryResponse>(
        API_ENDPOINTS.LIBRARY,
        { signal: options?.signal }
      )

      return {
        success: true,
        data: response.presentations,
      }
    } catch (error) {
      // Backend failed - try falling back to static data
      console.warn('[PresentationService] Backend unavailable - attempting fallback to static data')

      try {
        const staticPresentations = getStaticPresentationLibrary()
        console.log('[PresentationService] Successfully loaded static presentations as fallback')

        return {
          success: true,
          data: staticPresentations,
        }
      } catch (_staticError) {
        // Both backend and static data failed
        return {
          success: false,
          error: error instanceof PresentationServiceError
            ? error
            : new PresentationServiceError(
                error instanceof Error ? error.message : 'Unknown error',
                'FETCH_ERROR'
              ),
        }
      }
    }
  }

  /**
   * Fetch a single presentation by slug
   *
   * This method:
   * 1. If offline mode is enabled, skips backend access control
   * 2. Otherwise checks access control via the backend API (if enabled)
   * 3. Loads the actual presentation content from TypeScript files
   * 4. Returns error if user doesn't have access (403)
   *
   * @param slug - The presentation slug/ID
   * @returns Result with presentation data or error
   */
  async fetchBySlug(slug: string, options?: RequestOptions): Promise<ServiceResult<PresentationData>> {
    // If offline mode is enabled, skip backend access control and load directly from static files
    if (isOfflineModeEnabled()) {
      console.log(`[PresentationService] Offline mode - loading presentation "${slug}" from static files`)
      try {
        const staticPresentations = await this.loadStaticPresentations()
        const presentation = staticPresentations.find(p => p.slug === slug)

        if (!presentation) {
          return {
            success: false,
            error: new PresentationServiceError('Presentation not found', 'NOT_FOUND', 404),
          }
        }

        return {
          success: true,
          data: presentation.data,
        }
      } catch (error) {
        return {
          success: false,
          error: new PresentationServiceError(
            error instanceof Error ? error.message : 'Failed to load static presentation',
            'STATIC_LOAD_ERROR'
          ),
        }
      }
    }

    // If backend is enabled, check access control first
    if (API_CONFIG.USE_BACKEND) {
      try {
        // Check access control by fetching metadata from backend
        const response = await this.fetchWithRetry<RawPresentationMetadataResponse>(
          API_ENDPOINTS.PRESENTATION_BY_ID(slug),
          {
            signal: options?.signal,
            credentials: 'include', // Include authentication cookies
          }
        )

        // If access check failed, return error
        if (!response.success || !response.data) {
          const statusCode = response.error?.code === 'FORBIDDEN' ? 403 :
                            response.error?.code === 'NOT_FOUND' ? 404 : undefined

          return {
            success: false,
            error: new PresentationServiceError(
              response.error?.message || 'Failed to access presentation',
              response.error?.code || 'ACCESS_ERROR',
              statusCode
            ),
          }
        }

        // Access granted - metadata exists, now load actual presentation content from TypeScript files
        // The backend confirmed access, so we can safely load from static files
      } catch (error) {
        // Check if it's a 403 error (no access)
        if (error instanceof PresentationServiceError && error.statusCode === 403) {
          return {
            success: false,
            error: error,
          }
        }

        // For other errors (backend unavailable), log and try to load from static files
        console.warn(`[PresentationService] Backend unavailable for "${slug}" - attempting fallback to static files`)
      }
    }

    // Load presentation content from TypeScript files (static presentations)
    // This is safe because:
    // 1. If backend is enabled and accessible, we already checked access above
    // 2. If backend is disabled/unavailable, we fall back to static files
    // 3. If offline mode is enabled, we already returned above
    const staticPresentations = await this.loadStaticPresentations()
    const presentation = staticPresentations.find(p => p.slug === slug)

    if (!presentation) {
      return {
        success: false,
        error: new PresentationServiceError('Presentation not found', 'NOT_FOUND', 404),
      }
    }

    return {
      success: true,
      data: presentation.data,
    }
  }

  /**
   * Load static presentations from TypeScript files
   * @private
   */
  private async loadStaticPresentations(): Promise<PresentationLibraryEntry[]> {
    // Dynamic import to avoid circular dependencies
    const { staticPresentations } = await import('../presentations')
    // Transform PresentationEntry[] to PresentationLibraryEntry[]
    return staticPresentations.map(entry => ({
      slug: entry.slug,
      data: entry.presentation,
    }))
  }

  /**
   * Save a presentation to the backend (requires authentication)
   *
   * @param slug - The presentation slug
   * @param data - The presentation data to save
   * @param token - Authentication token
   * @returns Result with updated presentation data or error
   */
  async savePresentation(
    slug: string,
    data: PresentationData,
    token: string | null,
    options?: RequestOptions
  ): Promise<ServiceResult<PresentationData>> {
    try {
      if (!token) {
        return {
          success: false,
          error: new PresentationServiceError('Authentication required', 'UNAUTHORIZED', 401),
        }
      }

      const response = await this.fetchWithRetry<RawApiResponse<PresentationData>>(
        API_ENDPOINTS.PRESENTATION_BY_SLUG(slug),
        {
          method: 'PUT',
          signal: options?.signal,
          headers: {
            'Authorization': `Bearer ${token}`,
            ...options?.headers,
          },
          body: JSON.stringify({ data }),
        }
      )

      if (!response.success) {
        return {
          success: false,
          error: new PresentationServiceError(
            response.error?.message || 'Failed to save presentation',
            response.error?.code
          ),
        }
      }

      return {
        success: true,
        data: response.data!,
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof PresentationServiceError
          ? error
          : new PresentationServiceError(
              error instanceof Error ? error.message : 'Unknown error',
              'SAVE_ERROR'
            ),
      }
    }
  }

  /**
   * Create a new presentation (requires authentication)
   *
   * @param slug - The presentation slug
   * @param data - The presentation data
   * @param token - Authentication token
   * @returns Result with created presentation data or error
   */
  async createPresentation(
    slug: string,
    data: PresentationData,
    token: string | null,
    options?: RequestOptions
  ): Promise<ServiceResult<PresentationData>> {
    try {
      if (!token) {
        return {
          success: false,
          error: new PresentationServiceError('Authentication required', 'UNAUTHORIZED', 401),
        }
      }

      const response = await this.fetchWithRetry<RawApiResponse<PresentationData>>(
        API_ENDPOINTS.CREATE,
        {
          method: 'POST',
          signal: options?.signal,
          headers: {
            'Authorization': `Bearer ${token}`,
            ...options?.headers,
          },
          body: JSON.stringify({ slug, data }),
        }
      )

      if (!response.success) {
        return {
          success: false,
          error: new PresentationServiceError(
            response.error?.message || 'Failed to create presentation',
            response.error?.code
          ),
        }
      }

      return {
        success: true,
        data: response.data!,
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof PresentationServiceError
          ? error
          : new PresentationServiceError(
              error instanceof Error ? error.message : 'Unknown error',
              'CREATE_ERROR'
            ),
      }
    }
  }

  /**
   * Delete a presentation (requires authentication)
   *
   * @param id - The presentation ID
   * @param token - Authentication token
   * @returns Result indicating success or error
   */
  async deletePresentation(
    id: string,
    token: string | null,
    options?: RequestOptions
  ): Promise<ServiceResult<void>> {
    try {
      if (!token) {
        return {
          success: false,
          error: new PresentationServiceError('Authentication required', 'UNAUTHORIZED', 401),
        }
      }

      const response = await this.fetchWithRetry<RawApiResponse<void>>(
        API_ENDPOINTS.DELETE(id),
        {
          method: 'DELETE',
          signal: options?.signal,
          headers: {
            'Authorization': `Bearer ${token}`,
            ...options?.headers,
          },
        }
      )

      if (!response.success) {
        return {
          success: false,
          error: new PresentationServiceError(
            response.error?.message || 'Failed to delete presentation',
            response.error?.code
          ),
        }
      }

      return { success: true, data: undefined }
    } catch (error) {
      return {
        success: false,
        error: error instanceof PresentationServiceError
          ? error
          : new PresentationServiceError(
              error instanceof Error ? error.message : 'Unknown error',
              'DELETE_ERROR'
            ),
      }
    }
  }
}

/**
 * Singleton instance of the presentation service
 */
export const presentationService = new PresentationService()
